import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { v4 as randomUUID } from "uuid";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import ReviewerAutocomplete from "./ReviewerAutocomplete";

import { useRuleContext } from "../../../context/ruleContext";
import { Supervisor } from "@dashboard-v3/api";
import {
  addKeys,
  extractKeys,
  ListWithKey,
  removeItem,
  replace,
} from "utils/listWithKey";
import { getApprovalSteps } from "pages/Rules/Form/context/ruleSelectors";

export default function StepsList() {
  const { t } = useTranslation("rules");
  const classes = useStyles();
  const { state, dispatch } = useRuleContext();
  const [supervisors, setSupervisors] = useState<ListWithKey<Supervisor>>(
    addKeys(getApprovalSteps(state.rule).supervisors, () => randomUUID())
  );

  const canAddReviewer = supervisors.length < 10;

  function dispatchUpdate(updated: ListWithKey<Supervisor>) {
    setSupervisors(updated);
    dispatch({
      type: "updateStep",
      payload: {
        step: "approvalSteps",
        changes: { supervisors: extractKeys(updated) },
      },
    });
  }

  function updateSupervisor(key: string, updated: Supervisor) {
    const updatedList = replace(supervisors, key, updated);
    dispatchUpdate(updatedList);
  }

  function addReviewer() {
    if (!canAddReviewer) return;

    const updatedList = [
      ...supervisors,
      { key: randomUUID(), value: { entity: null, type: null } },
    ];

    dispatchUpdate(updatedList);
  }

  function removeReviewer(key: string) {
    const updatedList = removeItem(supervisors, key);
    dispatchUpdate(updatedList);
  }

  function getStepTranslation(
    partial: "stepTitle" | "stepDesc",
    supervisor: Supervisor
  ) {
    const { type } = supervisor;

    if (!type) return t(`approvalSteps.list.${partial}.default`);

    return t(`approvalSteps.list.${partial}.${type}`);
  }

  return (
    <div className={classes.listContainer}>
      <div className={classes.listHeaderContainer}>
        <div className={classes.headerTitleContainer}>
          <Typography
            className={classes.headerTitle}
            variant="subtitle1"
            color="textPrimary"
          >
            {t("approvalSteps.list.title")}
          </Typography>
          <Typography
            className={classes.headerSubtitle}
            variant="caption"
            color="textSecondary"
          >
            {t("approvalSteps.list.desc")}
          </Typography>
        </div>
      </div>
      <Stepper className={classes.stepper} orientation="vertical" nonLinear>
        {supervisors.map(({ key, value: supervisor }, index) => {
          return (
            <Step
              key={key}
              expanded={true}
              active={!!supervisor.entity}
              style={{
                marginBottom: isLastStep(supervisors, index)
                  ? "70px"
                  : "initial",
              }}
            >
              <StepLabel className={classes.stepLabel}>
                <div style={{ marginLeft: 8 }}>
                  <Typography variant="subtitle2">
                    {getStepTranslation("stepTitle", supervisor)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {getStepTranslation("stepDesc", supervisor)}
                  </Typography>
                </div>
                {isLastStep(supervisors, index) && (
                  <div
                    className={classes.addBtnContainer}
                    onClick={addReviewer}
                  >
                    <div className={classes.addBtnSeparator} />
                    <AddCircleIcon
                      className={classes.addBtn}
                      color={canAddReviewer ? "primary" : "disabled"}
                    />
                    <Typography
                      className={classes.addBtnLabel}
                      variant="caption"
                      color={canAddReviewer ? "primary" : "textSecondary"}
                    >
                      {t("approvalSteps.addReviewerBtn")}
                    </Typography>
                  </div>
                )}
              </StepLabel>
              <StepContent className={classes.stepContent}>
                <ReviewerAutocomplete
                  key={key}
                  value={supervisor}
                  onChange={updated => updateSupervisor(key, updated)}
                  onRemoveReviewer={() => removeReviewer(key)}
                />
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}

const isLastStep = (options: ListWithKey<Supervisor>, index: number) => {
  return options.length - 1 === index;
};

const useStyles = makeStyles({
  listContainer: { display: "flex", flexDirection: "column", gap: 10 },
  listHeaderContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  headerTitleContainer: {
    maxWidth: "85%",
    display: "flex",
    flexDirection: "column",
  },
  headerTitle: { fontWeight: 500 },
  headerSubtitle: { marginBottom: 15 },
  stepper: {
    padding: 0,
    "& .MuiStepConnector-root.MuiStepConnector-vertical": {
      padding: 0,
    },
  },
  stepLabel: { position: "relative", width: "90%" },
  stepContent: { marginTop: 0 },
  addBtnContainer: { position: "absolute", left: "-1.8px" },
  addBtnSeparator: {
    height: "95px",
    width: "50%",
    marginTop: "3px",
    marginBottom: "10px",
    borderRight: "1px dashed #8f8f8f",
  },
  addBtn: { fontSize: "1.8rem", cursor: "pointer" },
  addBtnLabel: {
    position: "absolute",
    width: 100,
    bottom: 8.5,
    left: 35,
    fontWeight: 500,
    cursor: "pointer",
  },
});
